import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Bank } from './bank.model';
import { BankService } from './bank.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-holl-bank-management',
  templateUrl: './holl-bank-management.component.html',
  styleUrls: ['./holl-bank-management.component.scss']
})
export class HollBankManagementComponent implements OnInit {

  banks: Bank[] = [];
  filteredBanks: Bank[] = [];
  searchQuery = '';
  isLoading = false;
  selectedBank: Bank | null = null;
  viewMode: 'list' | 'detail' | 'form' = 'list';
  isEditing = false;

  // Pagination
  currentPage = 0;
  pageSize = 6;
  totalItems = 0;
  totalPages = 0;

  // Form model
  bankForm: Bank = this.getEmptyBank();

  // Validation state
  formErrors: { [key: string]: string } = {};
  formTouched: { [key: string]: boolean } = {};

  constructor(private router: Router, private bankService: BankService) {}

  ngOnInit() {
    this.loadBanks();
  }

  getEmptyBank(): Bank {
    return {
      bankName: '',
      contactPersonName: '',
      contactPersonEmail: '',
      contactPersonPhone: '',
      bankLogoUrl: '',
      deleted: false
    };
  }

  // ── Load banks from API ───────────────────────────────────
  loadBanks() {
    this.isLoading = true;

    this.bankService.getAllBanks(this.currentPage, this.pageSize).subscribe(
      (response) => {
        if (response.responseCode === '000') {
          this.banks = response.data.banks || [];
          this.totalItems = response.data.totalItems;
          this.totalPages = response.data.totalPages;
          this.currentPage = response.data.currentPage;
          this.filteredBanks = this.banks.slice();
        } else {
          Swal.fire({ title: 'Error', text: response.responseMessage || 'Failed to load banks.', icon: 'error' });
        }
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading banks:', error);
        Swal.fire({ title: 'Error', text: 'Unable to connect to the server. Please try again later.', icon: 'error' });
        this.isLoading = false;
      }
    );
  }

  // ── Search (client-side filter on loaded data) ────────────
  onSearch() {
    const q = this.searchQuery.toLowerCase().trim();
    if (!q) {
      this.filteredBanks = this.banks.slice();
      return;
    }
    this.filteredBanks = this.banks.filter(b =>
      b.bankName.toLowerCase().includes(q) ||
      b.contactPersonName.toLowerCase().includes(q) ||
      b.contactPersonEmail.toLowerCase().includes(q) ||
      b.contactPersonPhone.includes(q)
    );
  }

  // ── View bank detail ──────────────────────────────────────
  viewBank(bank: Bank) {
    this.selectedBank = Object.assign({}, bank);
    this.viewMode = 'detail';
  }

  // ── Open form for creating ────────────────────────────────
  openCreateForm() {
    this.bankForm = this.getEmptyBank();
    this.formErrors = {};
    this.formTouched = {};
    this.isEditing = false;
    this.viewMode = 'form';
  }

  // ── Open form for editing ─────────────────────────────────
  openEditForm(bank: Bank) {
    this.bankForm = Object.assign({}, bank);
    this.formErrors = {};
    this.formTouched = {};
    this.isEditing = true;
    this.viewMode = 'form';
  }

  // ── Back to list ──────────────────────────────────────────
  backToList() {
    this.viewMode = 'list';
    this.selectedBank = null;
  }

  // ── Validation ────────────────────────────────────────────
  markTouched(field: string) {
    this.formTouched[field] = true;
    this.validateField(field);
  }

  validateField(field: string) {
    const val = this.bankForm[field];

    switch (field) {
      case 'bankName':
        if (!val || !val.trim()) {
          this.formErrors[field] = 'Bank name is required.';
        } else if (val.trim().length < 2) {
          this.formErrors[field] = 'Bank name must be at least 2 characters.';
        } else if (val.trim().length > 512) {
          this.formErrors[field] = 'Bank name must not exceed 512 characters.';
        } else {
          delete this.formErrors[field];
        }
        break;

      case 'contactPersonName':
        if (!val || !val.trim()) {
          this.formErrors[field] = 'Contact person name is required.';
        } else if (val.trim().length < 2) {
          this.formErrors[field] = 'Name must be at least 2 characters.';
        } else if (val.trim().length > 512) {
          this.formErrors[field] = 'Name must not exceed 512 characters.';
        } else {
          delete this.formErrors[field];
        }
        break;

      case 'contactPersonEmail':
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!val || !val.trim()) {
          this.formErrors[field] = 'Email address is required.';
        } else if (!emailRegex.test(val.trim())) {
          this.formErrors[field] = 'Please enter a valid email address.';
        } else if (val.trim().length > 45) {
          this.formErrors[field] = 'Email must not exceed 45 characters.';
        } else {
          delete this.formErrors[field];
        }
        break;

      case 'contactPersonPhone':
        const phoneRegex = /^\+?[0-9]{10,20}$/;
        if (!val || !val.trim()) {
          this.formErrors[field] = 'Phone number is required.';
        } else if (!phoneRegex.test(val.trim().replace(/\s/g, ''))) {
          this.formErrors[field] = 'Please enter a valid phone number (e.g. +233201234567).';
        } else {
          delete this.formErrors[field];
        }
        break;

      case 'bankLogoUrl':
        if (val && val.trim()) {
          const urlRegex = /^https?:\/\/.+/;
          if (!urlRegex.test(val.trim())) {
            this.formErrors[field] = 'Please enter a valid URL (starting with http:// or https://).';
          } else if (val.trim().length > 1024) {
            this.formErrors[field] = 'URL must not exceed 1024 characters.';
          } else {
            delete this.formErrors[field];
          }
        } else {
          delete this.formErrors[field];
        }
        break;
    }
  }

  validateAll(): boolean {
    const fields = ['bankName', 'contactPersonName', 'contactPersonEmail', 'contactPersonPhone', 'bankLogoUrl'];
    fields.forEach(f => {
      this.formTouched[f] = true;
      this.validateField(f);
    });
    return Object.keys(this.formErrors).length === 0;
  }

  hasError(field: string): boolean {
    return this.formTouched[field] && !!this.formErrors[field];
  }

  // ── Save bank (Create / Update via API) ───────────────────
  saveBank() {
    if (!this.validateAll()) {
      return;
    }

    this.isLoading = true;

    if (this.isEditing) {
      this.bankService.updateBank(this.bankForm.bankID, this.bankForm).subscribe(
        (response) => {
          this.isLoading = false;
          if (response.responseCode === '000') {
            Swal.fire({ title: 'Success!', text: '"' + this.bankForm.bankName + '" has been updated successfully.', icon: 'success' });
            this.viewMode = 'list';
            this.loadBanks();
          } else {
            Swal.fire({ title: 'Error', text: response.responseMessage || 'Failed to update bank.', icon: 'error' });
          }
        },
        (error) => {
          this.isLoading = false;
          console.error('Error updating bank:', error);
          Swal.fire({ title: 'Error', text: 'Unable to update bank. Please try again.', icon: 'error' });
        }
      );
    } else {
      this.bankService.createBank(this.bankForm).subscribe(
        (response) => {
          this.isLoading = false;
          if (response.responseCode === '000') {
            Swal.fire({ title: 'Success!', text: '"' + this.bankForm.bankName + '" has been created successfully.', icon: 'success' });
            this.viewMode = 'list';
            this.loadBanks();
          } else {
            Swal.fire({ title: 'Error', text: response.responseMessage || 'Failed to create bank.', icon: 'error' });
          }
        },
        (error) => {
          this.isLoading = false;
          console.error('Error creating bank:', error);
          Swal.fire({ title: 'Error', text: 'Unable to create bank. Please try again.', icon: 'error' });
        }
      );
    }
  }

  // ── Delete bank via API ───────────────────────────────────
  deleteBank(bank: Bank) {
    Swal.fire({
      title: 'Delete Bank?',
      text: `Are you sure you want to delete "${bank.bankName}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#F05B24',
      cancelButtonColor: '#a0a0b0',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.value) {
        this.isLoading = true;

        this.bankService.deleteBank(bank.bankID).subscribe(
          (response) => {
            this.isLoading = false;
            if (response.responseCode === '000') {
              if (this.selectedBank && this.selectedBank.bankID === bank.bankID) {
                this.viewMode = 'list';
                this.selectedBank = null;
              }
              Swal.fire({ title: 'Deleted!', text: `"${bank.bankName}" has been removed.`, icon: 'success' });
              this.loadBanks();
            } else {
              Swal.fire({ title: 'Error', text: response.responseMessage || 'Failed to delete bank.', icon: 'error' });
            }
          },
          (error) => {
            this.isLoading = false;
            console.error('Error deleting bank:', error);
            Swal.fire({ title: 'Error', text: 'Unable to delete bank. Please try again.', icon: 'error' });
          }
        );
      }
    });
  }

  // ── Pagination ─────────────────────────────────────────────
  goToPage(page: number) {
    if (page < 0 || page >= this.totalPages || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.loadBanks();
  }

  nextPage() {
    this.goToPage(this.currentPage + 1);
  }

  prevPage() {
    this.goToPage(this.currentPage - 1);
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    let start = Math.max(0, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(this.totalPages, start + maxVisible);

    // Adjust start if we're near the end
    if (end - start < maxVisible) {
      start = Math.max(0, end - maxVisible);
    }

    for (let i = start; i < end; i++) {
      pages.push(i);
    }
    return pages;
  }

  // ── Helpers ───────────────────────────────────────────────
  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0].substring(0, 2).toUpperCase();
  }

  formatDate(dateStr: string, includeTime: boolean = false): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const datePart = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    if (!includeTime) return datePart;
    const timePart = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${datePart}, ${timePart}`;
  }
}
